import LoadingOverlay from "components/blocks/LoadingOverlay";
import Grid from "components/GridView/components/Grid";
import { GridSchema } from "components/GridView/GridView.types";
import { useCookieContext } from "contexts";
import { useNotification } from "hooks/useNotification";
import useSystemDateTheme from "hooks/useSystemDateTheme";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {CourseLocalResult, CourseResult} from "services/courseService/models/CourseType";
import {BackendServices} from "../../../../services";
import {NotesType} from "../OrderView/tabs/OrderInfoTab/components/WorkFlowNotes";
import AuthGuard from "../../../../components/blocks/AuthGuard";
import IconButton from "../../../../components/blocks/IconButton";
import Modal from "../../../../components/blocks/Modal";
import {FileResult} from "../../../../services/shared-models/sharedTypes";
import {CourseOrderActionResult} from "../../../../services/courseOrder/models/CourseOrderType";
import AttachmentCard from "../../../../components/blocks/AttachmentCard";

type OrderActionColumn =
  | "notes"
  | "workflowName"
  | "action"
  | "processInstanceId"
  | "folio"
  | "appliedBy"
  | "createdOn"
  | "status";

type OrderActionsProps = {
  visibleColumns: OrderActionColumn[];
  orderId: number;
};

const OrderActionsGrid = ({ visibleColumns, orderId }: OrderActionsProps) => {
  const { t } = useTranslation("OrderActionsGrid");
  const { showNotification } = useNotification();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<any[]>([]);
  const { toSystemThemeDateFormat } = useSystemDateTheme();
  const { isAr } = useCookieContext();
  const [showModalProps, setShowModalProps] = useState<{
    isOpen: boolean;
    data?: FileResult[] 
  }>({
    isOpen: false,
    data: [],
  });

  useEffect(() => {
    if (orderId) fetchOrderActions();
  }, [isAr, orderId]);

  const fetchOrderActions = async () => {
    try {
      setLoading(true);
      const res = await BackendServices.courseOrderService.getAllActionsCourseOrderByOrderId(orderId); 
      if(!res.hasError)
      {
        setData(res.data);
      }
      
      setLoading(false);
    } catch (error) {
      showNotification({
        type: "error",
        description: t("errorGettingOrderActions"),
      });
    } finally {
      setLoading(false);
    }
  };

  const gridSchema = useMemo<GridSchema[]>(() => {
    return [
      {
        field: "notes",
        displayName: t("notes"),
        isHidden: !visibleColumns.includes("notes"),
      },
      {
        field: "action",
        displayName: t("action"),
        showOnMobile: true,
        render: (row) => t(row.action.toString()),
        isHidden: !visibleColumns.includes("action"),
      },
      {
        field: "processInstanceId",
        displayName: t("processInstanceId"),
        isHidden: !visibleColumns.includes("processInstanceId"),
      },
      {
        field: "folio",
        displayName: t("folio"),
        isHidden: !visibleColumns.includes("folio"),
      },
      {
        field: "activity",
        displayName: t("activityName"),
        render: (row) =>row?.activity?.activityName,
      },
      {
        field: "workflowName",
        displayName: t("workflowName"),
        isHidden: !visibleColumns.includes("workflowName"),
      },
     
      {
        field: "appliedBy",
        displayName: t("appliedBy"),
        render: (row) => (isAr ? row.appliedBy.arabicName : row.appliedBy.englishName),
        isHidden: !visibleColumns.includes("appliedBy"),
      },
      {
        field: "createdOn",
        displayName: t("createdOn"),
        render: (row) => toSystemThemeDateFormat(row.createdOn),
        isHidden: !visibleColumns.includes("createdOn"),
      },
      {
        field: "status",
        displayName: t("status"),
        showOnMobile: true,
        render: (row) => {
          const statusClassName =
            row.status === "Completed"
              ? "bg-success bg-opacity-10 text-success"
              : "bg-primary bg-opacity-10 text-primary";
          return <span className={`badge rounded-4 ${statusClassName} py-2`}>{t(`Common:${row.status}`)}</span>;
        },
        isHidden: !visibleColumns.includes("status"),
      },
      {
        field: "actions",
        displayName: t("Common:actions"),
        showOnMobile: true,
        render: (row:CourseOrderActionResult ) => {
          return (
              <span className="d-flex justify-content-center gap-2">
               <IconButton
                   fitToIconWidth
                   icon="icon-eye"
                   innerIconColor="primary"
                   size="md"
                   onClick={() => {
                      setShowModalProps({
                        isOpen: true,
                        data: row.attachments || [],
                      })
                   }}
               />
            </span>
          );
        },
      }, 
        
    ];
  }, [visibleColumns, t]);

  return (
    <div className="position-relative">
      {loading && <LoadingOverlay />}
      <Grid gridSchema={gridSchema} data={data} />

      <Modal
          containerClassName="bg-white pb-2"
          isOpen={showModalProps.isOpen}
          onClose={() => {
            setShowModalProps({ isOpen: false, data: [] });
          }}
          title={t("Common:attachments")}
          withHeaderBorder
          size="lg"
          titleClassName="fs-6 text-dark"
      >
            <div className="d-flex gap-2 flex-wrap">
              {showModalProps.data && showModalProps.data.length > 0 
                  ? showModalProps.data.map((file: FileResult, index: number) => (
                      <AttachmentCard
                          attachment={{
                            extension: file.extension ?? "",
                            fileName: file.name ?? "",
                            id: file.id ?? "",
                            size: file.size ?? "",
                          }}
                      />
                  ))
                  : t("Common:noData")}
            </div>
        
      </Modal>
    </div>
      
      
  );
};

export default OrderActionsGrid;
